"use strict";
const _ = require("lodash");
const Veil = require("@gluenetworks/local_veil_gl");
const ModalLoadingComponent = require("./loading/ModalLoadingComponent");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./UserFeedbackRenderer");

const Confirmation = require("./confirmation/ModalConfirmationComponent");
const ErrorCmpnt = require("./error/ModalErrorComponent");
const NotificationCmpnt = require("./notification/ModalNotificationComponent");

/**
 * @typedef ActionDefinition
 * @type {object}
 * @description Definition details about the action.
 * @property {string} [type='text'] - The type property identifies what type this action is. Supported types are text|html|icon|class|additive|destructive
 * @property {string} [text='Yes'] - Display this text as the action to the user, type must be text|additive|destructive.
 * @property {string} [html] - Display this html as the action to the user, type must be html.
 * @property {object} [iconDetails] - Display the icon detailed in this object, type must be icon.
 *      @property {string} [iconDetails.iconPath] - The fully qualified path to the icon.svg.js.
 *      @property {string} [iconDetails.iconType] - The name of the icon Type.
 * @property {string|array} [classes] - A string space delimited of classes or an array of strings(not space delimited) that will be applied to the action, type must be class.
 */
/**
 * @typedef UserFeedbackContext
 * @type {object}
 * @property {string} type - The type of the User Feedback Context object.
 * @property {function} resolve - A function to resolve the promise.
 * @property {function} reject - A function to reject the promise.
 * @property {UserFeedback} api - The UserFeedback object that launched the feeback context.
 */
/**
 * @typedef DisplayOptions
 * @property {boolean} [notDismissable=false] - If true will not allow the user to dismiss the veil.
 */

/**
 * @typedef TeardownOptions
 * @property {boolean} [dontDestroy=false] - If true will not call shutdown on the component.
 * @property {boolean} [dontHide=false] - If true will not hide the veil during the teardown process.
 */
/**
 * This Component implements the User Feedback interface and exposes
 * the veil API for direct use.
 * @implements UserFeedback
 */
class UserFeedbackComponent extends EventEmitter {
    constructor() {
        super();
        this._veil = new Veil({});

        this._displayed = undefined;

        this._renderer = new Renderer(this);

        this._registerListeners();
        this._renderer.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    async shutdown() {
        this._unregisterListeners();
        let promArr = [];
        if (this.isDisplayed()) {
            // promArr.push(this._displayed.shutdown());
            await this.hideDisplayed();
        }
        promArr.push(this._renderer.shutdown());
        promArr.push(this._veil.shutdown());

        return Promise.all(promArr).then(() => {
            this._displayed = undefined;
            this._veil = undefined;
            this._renderer = undefined;
        });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /**
     * Display a component to the user
     * @param {Component} cmpnt - the compnent to render in the modal.
     * @param {DisplayOptions} [opts] - The display options to be used.
     * @returns {Promise}
     * @private
     */
    _display(cmpnt, opts) {
        let userClosedHdlr;
        let prom = new Promise((resolve, reject) => {
            let userReject = (err) => {
                if (err) {
                    reject(err);
                } else {
                    reject({userRejected: true});
                }
            };

            let uf = {
                type: "UserFeedbackComponent",
                resolve: resolve,
                reject: userReject,
                api: this,
            };
            this._veil.on(
                "UserHide",
                (userClosedHdlr = () => {
                    reject({userRejected: true});
                })
            );
            _.set(cmpnt, "$teve.ui.context", uf);

            if (_.get(opts, "notDismissable")) {
                this._veil.setUnclosable(true);
            } else {
                this._veil.setUnclosable(false);
            }

            //If a component is currently displayed or the veil was left up.
            if (this.isStillOpen) {
                let oldCmpnt = this._displayed;
                if (this._displayed) {
                    this._displayed.shutdown();
                }
                this._displayed = cmpnt;
                this._veil.setContent(cmpnt, {dontDestroy: true});
                this.emit("DisplaySwapped", cmpnt, oldCmpnt);
            } else {
                this._displayed = cmpnt;
                this._veil.setContent(cmpnt, {dontDestroy: true});
                this._veil.show().then(() => {
                    this.emit("Displayed", cmpnt);
                });
            }
        });

        this._veil.once("Hide", () => {
            this._veil.removeListener("UserHide", userClosedHdlr);
        });

        prom.then(() => {
            this._veil.removeListener("UserHide", userClosedHdlr);
            this._teardown(opts);
        }).catch(() => {
            this._veil.removeListener("UserHide", userClosedHdlr);
            //remove the dont'Hide on rejection
            let optsHideOverride = _.defaults({}, opts, {dontHide: false});
            this._teardown(optsHideOverride);
        });
        return prom;
    }

    /**
     * Teardown the current displayed component.
     * @param {TeardownOptions} opts - The display options to be used.
     * @private
     */
    _teardown(opts) {
        return new Promise((resolve) => {
            if (this.isStillOpen) {
                let oldCmpnt = this._displayed;
                this._displayed = null;
                let shutdownProm = Promise.resolve();

                if (oldCmpnt && !_.get(opts, "dontDestroy")) {
                    shutdownProm = oldCmpnt.shutdown();
                }
                if (!_.get(opts, "dontHide")) {
                    this._veil.hide().then(() => {
                        this.emit("Hidden", oldCmpnt);
                        shutdownProm.then(() => {
                            //turn the event loop to make sure everything gets cleaned up
                            setTimeout(() => {
                                resolve();
                            }, 0);
                        });
                    });
                } else {
                    shutdownProm.then(() => {
                        resolve();
                    });
                }
            } else {
                resolve();
            }
        });
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    isDisplayed() {
        if (this._displayed) {
            return true;
        } else {
            return false;
        }
    }

    get isStillOpen() {
        return (
            this.isDisplayed() ||
            (this._veil && typeof this._veil.isShown === "function" && this._veil.isShown()) ||
            false
        );
    }

    /*******************************************/
    /* END PUBLIC METHODS                      */
    /* START USER FEEDBACK API METHODS         */
    /*******************************************/

    /**
     * Displays a confirmation message to the user.
     * @param {string} msg - Message to present to the user.
     * @param {object} [opts] - Optional parameters.
     * @param {string} [title=undefined] - A Title to present to the user.
     *
     * @param {string} [title=Please Confirm] - Optional Title of the Confirmation.
     * @param {ActionDefinition} [confirmDetails=undefined] - Details about the confirm action definition.
     * @param {ActionDefinition} [rejectDetails=undefined] - Details about the reject action definition.
     */
    displayConfirmation(msg, opts) {
        let cmpnt = new Confirmation(msg, opts);
        return this._display(cmpnt, opts);
    }

    /**
     * Displays an error notification to the user.
     * @param {object} [opts] - Optional parameters.
     * @param {GlueErrorObject} [opts.error] - The Glue Error object to be set.
     * @param {string} [opts.errorCode='UNKNOWN_ERROR'] - The Glue Error Code to be set.
     * @param {string} [opts.errorMsg='We have encountered an unknown error.'] - The Glue Error message to be set.
     * @param {string} [title='Error Encountered'] - A Title to present to the user.
     *
     */
    displayError(opts) {
        let errorObjOpts = _.defaults(
            {},
            _.get(opts, "error"),
            {
                errorCode: _.get(opts, "errorCode"),
                errorMsg: _.get(opts, "errorMsg"),
                errorTitle: _.get(opts, "title"),
            },
            {
                errorCode: "UNKNOWN_ERROR",
                errorMsg: "We have encountered an unknown error.",
                errorTitle: "Error Encountered",
            }
        );
        errorObjOpts.confirmDetails = _.defaults({}, _.get(opts, "confirmDetails"), {
            type: "text",
            text: "OK",
        });
        let cmpnt = new ErrorCmpnt(errorObjOpts);
        return this._display(cmpnt, opts);
    }

    /**
     * Displays a notification to the user.
     * @param {string} msg - Message to present to the user.
     * @param {object} [opts] - Optional parameters.
     * @param {string} [title=undefined] - A Title to present to the user.
     *
     * @param {string} [title=Notification] - Optional Title of the Confirmation.
     * @param {ActionDefinition} [confirmDetails=undefined] - Details about the confirm action definition.
     */
    displayNotification(msg, opts) {
        let cmpnt = new NotificationCmpnt(msg, opts);
        return this._display(cmpnt, opts);
    }

    /**
     * Displays a custom component to the user.
     * @param {Component} cstmComponent - the compnent to render in the modal.
     * @param {DisplayOptions} opts - The display options to be used.
     * @returns {Promise} when feedback is completed, rejected if
     */
    displayCustom(cstmComponent, opts) {
        return this._display(cstmComponent, opts);
    }

    /**
     * Displays a loading veil with an optional spinner message and action buttons.
     * @param {object} [opts] - Optional parameters.
     * @param {string} [opts.message] - Message shown below the spinner.
     * @param {boolean} [opts.showHideButton] - Show a Hide button that auto-dismisses the veil on click.
     * @param {function} [opts.onHide] - Called when the Hide button is clicked, before dismissing.
     * @param {boolean} [opts.showAbortButton] - Show an Abort button (caller manages teardown).
     * @param {function} [opts.onAbort] - Called when the Abort button is clicked.
     * @param {string} [opts.abortLabel] - Label for the Abort button (defaults to "Abort").
     */
    displayLoading(opts) {
        return new Promise((loaderResolve) => {
            let displayedHdlr;
            let swappedHdlr;

            const cmpnt = new ModalLoadingComponent(opts);
            const overriddenOpts = _.merge({notDismissable: true}, opts);

            this.once(
                "Displayed",
                (displayedHdlr = () => {
                    this.removeListener("DisplaySwapped", swappedHdlr);
                    loaderResolve();
                })
            );
            this.once(
                "DisplaySwapped",
                (swappedHdlr = () => {
                    this.removeListener("Displayed", displayedHdlr);
                    loaderResolve();
                })
            );
            this._display(cmpnt, overriddenOpts);
        });
    }

    showLoadingProgress(msg) {
        if (this._displayed && _.isFunction(this._displayed.showProgress)) {
            this._displayed.showProgress(msg);
        }
    }

    hideDisplayed() {
        return this._teardown({dontHide: false});
    }

    /*******************************************/
    /* END USER FEEDBACK API METHODS           */
    /*******************************************/
}

module.exports = UserFeedbackComponent;
